import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import Dashboard from './pages/Dashboard';
import Knowledge from './pages/Knowledge';
import Settings from './pages/Settings';
import Finances from './pages/Finances';
import Referrals from './pages/Referrals';
import Chat from './pages/Chat';
import VoiceCoach from './pages/VoiceCoach';
import VideoCoach from './pages/VideoCoach';
import RecordedSessions from './pages/RecordedSessions';
import DialogHistory from './pages/DialogHistory';
import CoachesList from './pages/CoachesList';
import CoachProfile from './pages/CoachProfile';
import AvatarDemo from './pages/AvatarDemo';
import ExpertsPage from './pages/ExpertsPage';
import AboutPage from './pages/AboutPage';
import UserSettings from './pages/UserSettings';
import Navbar from './components/Navbar';
import AdminCoaches from './pages/Admin/AdminCoaches';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminAdmins from './pages/Admin/AdminAdmins';
import AdminFinances from './pages/Admin/AdminFinances';
import ClientHistory from './pages/ClientHistory';
import ClientBilling from './pages/ClientBilling';
import StripeSuccess from './pages/StripeSuccess';
import StripeCancel from './pages/StripeCancel';

import AccessDenied from './components/AccessDenied';
import { CoachOnlyRoute, ClientOnlyRoute, AuthenticatedRoute, AdminOnlyRoute } from './components/RoleRoutes';
import RouteWatcher from './components/RouteWatcher';
import { AuthProvider, useAuth } from './services/AuthContext';
import { ThemeProvider } from './services/ThemeContext';
import { ratingsAPI } from './services/api';
import RatingModal from './components/Modals/RatingModal';

function AppContent() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [ratingCheck, setRatingCheck] = React.useState({ isOpen: false, dialogId: null });

  const showNavbar = location.pathname !== '/' && location.pathname !== '/experts' && location.pathname !== '/about';

  // Check for unrated session on login
  React.useEffect(() => {
    const checkLastSession = async () => {
      if (user && user.role === 'client') {
        try {
          const response = await ratingsAPI.checkLastSession(user.id);
          if (response.data && response.data.dialog_id && !response.data.has_rating) {
            setRatingCheck({ isOpen: true, dialogId: response.data.dialog_id });
          }
        } catch (error) {
          console.error('Error checking last session:', error);
        }
      }
    };

    if (!loading && user) {
      checkLastSession();
    }
  }, [user, loading]);

  // Prevent tab closure during conversations
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      const activePaths = ['/user/chat', '/user/voice', '/user/video'];
      const isChatting = activePaths.some(path => location.pathname.startsWith(path));
      
      if (isChatting) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location.pathname]);

  return (
    <div className="App min-h-screen bg-[var(--bg-main)] text-[var(--text-primary)] transition-colors duration-300">
      <RouteWatcher />
      {showNavbar && <Navbar />}
      <RatingModal 
        isOpen={ratingCheck.isOpen} 
        onClose={() => setRatingCheck({ ...ratingCheck, isOpen: false })} 
        dialogId={ratingCheck.dialogId}
      />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/experts" element={<ExpertsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="/avatar-demo" element={<AvatarDemo />} />
        <Route path="/stripe/success" element={<StripeSuccess />} />
        <Route path="/stripe/cancel" element={<StripeCancel />} />

        {/* Protected routes - Coach only */}
        <Route path="/coach/dashboard" element={<CoachOnlyRoute><Dashboard /></CoachOnlyRoute>} />
        <Route path="/coach/knowledge" element={<CoachOnlyRoute><Knowledge /></CoachOnlyRoute>} />
        <Route path="/coach/settings" element={<CoachOnlyRoute><Settings /></CoachOnlyRoute>} />
        <Route path="/coach/finances" element={<CoachOnlyRoute><Finances /></CoachOnlyRoute>} />
        <Route path="/coach/referrals" element={<CoachOnlyRoute><Referrals /></CoachOnlyRoute>} />
        <Route path="/coach/client/:clientId/history" element={<CoachOnlyRoute><ClientHistory /></CoachOnlyRoute>} />
        <Route path="/coach/history/:dialogId" element={<CoachOnlyRoute><DialogHistory /></CoachOnlyRoute>} />

        {/* Protected routes - Client only */}
        <Route path="/user/chat" element={<ClientOnlyRoute><Chat /></ClientOnlyRoute>} />
        <Route path="/user/chat/:coachId/:dialogId?" element={<ClientOnlyRoute><Chat /></ClientOnlyRoute>} />
        <Route path="/user/voice" element={<ClientOnlyRoute><VoiceCoach /></ClientOnlyRoute>} />
        <Route path="/user/voice/:coachId" element={<ClientOnlyRoute><VoiceCoach /></ClientOnlyRoute>} />
        <Route path="/user/video/:coachId" element={<ClientOnlyRoute><VideoCoach /></ClientOnlyRoute>} />
        <Route path="/user/sessions/:coachId" element={<ClientOnlyRoute><RecordedSessions /></ClientOnlyRoute>} />
        <Route path="/user/history/:dialogId" element={<ClientOnlyRoute><DialogHistory /></ClientOnlyRoute>} />
        <Route path="/user/coaches" element={<ClientOnlyRoute><CoachesList /></ClientOnlyRoute>} />
        <Route path="/user/coach/:coachId" element={<ClientOnlyRoute><CoachProfile /></ClientOnlyRoute>} />
        <Route path="/user/settings" element={<ClientOnlyRoute><UserSettings /></ClientOnlyRoute>} />
        <Route path="/user/billing" element={<ClientOnlyRoute><ClientBilling /></ClientOnlyRoute>} />

        {/* Protected routes - Admin only */}
        <Route path="/admin/coaches" element={<AdminOnlyRoute><AdminCoaches /></AdminOnlyRoute>} />
        <Route path="/admin/users" element={<AdminOnlyRoute><AdminUsers /></AdminOnlyRoute>} />
        <Route path="/admin/admins" element={<AdminOnlyRoute><AdminAdmins /></AdminOnlyRoute>} />
        <Route path="/admin/finances" element={<AdminOnlyRoute><AdminFinances /></AdminOnlyRoute>} />

        {/* Protected routes - Both roles can access */}
        <Route path="/dashboard" element={<AuthenticatedRoute><Dashboard /></AuthenticatedRoute>} />
        <Route path="/chat" element={<AuthenticatedRoute><Chat /></AuthenticatedRoute>} />

        {/* 404 - Catch all unknown routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
